// Moving things by hand, on the picture itself.
//
// The app authors a page as two keyframes — the transform the background
// starts at and the one it ends at, with the move's length between them
// (book_player/side_panels/background_panel.lua, whose `bgPhase` is the same
// switch). Here they are dragged rather than typed: pick the phase, drag the
// picture, roll the wheel to scale it, hold Shift to lock the drag to one
// axis and Alt to scale faster — the same modifiers features/editor.lua uses.
//
// What a press does depends on the tool in hand: Select drags the picture,
// Text moves and sizes the words, Tap area draws a new zone, and anything
// clicked becomes the inspector's subject.
//
// Every gesture writes through the product editor, so the inspector shows the
// numbers a drag produced and the two stay one document.
import React, { useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import { useImageSize } from "../../../Storefront/utils/images"
import { getContentLayout, getPageImage } from "../../../Player/layout"
import { ASPECT_RANGES, LAYOUTS, layerLayout } from "./editorModel"

const PHASES = ["start", "end"]
/**
 * Holding the pointer through a drag. A pointer that is already gone (a
 * synthetic event, a cancelled touch) throws rather than answering, and a
 * drag is not worth an exception.
 */
const capture = (event) => {
  try {
    event.currentTarget.setPointerCapture?.(event.pointerId)
  } catch {}
}
const release = (event) => {
  try {
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  } catch {}
}

const round = (value) => Math.round(value * 100) / 100

/** A pointer drag in element pixels, reported as deltas until it is let go. */
function useDrag(onMove, { onStart, onEnd } = {}) {
  const from = useRef(null)
  return {
    onPointerDown: (event) => {
      event.preventDefault()
      event.stopPropagation()
      capture(event)
      from.current = { x: event.clientX, y: event.clientY }
      onStart?.(event)
    },
    onPointerMove: (event) => {
      if (!from.current) return
      const dx = event.clientX - from.current.x
      const dy = event.clientY - from.current.y
      from.current = { x: event.clientX, y: event.clientY }
      onMove(dx, dy, event)
    },
    onPointerUp: (event) => {
      from.current = null
      release(event)
      onEnd?.(event)
    },
  }
}

export default function StageTools({
  editor,
  page,
  resolved,
  pageIndex,
  viewport,
  phase,
  onPhase,
  enabled = true,
  tool = "select",
  selection = { kind: "scene" },
  onSelect,
  onAddTrigger,
}) {
  const t = useProductEditorTexts()
  const layout = getContentLayout()
  // The document holds a content key where the player holds a url, and a
  // drag has to be measured against the picture the player is showing: its
  // natural size is what turns screen pixels into authored units. Measuring
  // the key instead left the ratio at 1, so a drag moved the picture by a
  // fraction of the distance the pointer travelled.
  const image = getPageImage(resolved || page)
  const size = useImageSize(image)
  const setPage = (path, value) => editor.set(`pages.${pageIndex}.${path}`, value)
  // a page may hold only the layout it was authored in
  const backgroundLayout = layerLayout(page?.background, layout)
  const subtitleLayout = layerLayout(page?.subtitle, layout)

  // Every hook runs on every render: the guard that skips the tools lives
  // below them, or the order of hooks changes with it (rules of hooks).
  const background = page?.background?.[backgroundLayout] || {}
  const transform = background[`${phase}Transform`] || {}
  const offset = transform.offsetPos || {}
  // the picture is laid out against its own width; a pixel of screen is this
  // much of the authored space
  const ratio = size.width ? viewport.width / size.width : 1
  const design = viewport.width / 1000

  // Shift locks a drag to the axis it started along (features/editor.lua)
  const axisLock = useRef(null)
  const [drawing, setDrawing] = useState(null)

  const moveBackground = (dx, dy, event) => {
    if (event?.shiftKey) {
      if (!axisLock.current) axisLock.current = Math.abs(dx) > Math.abs(dy) ? "x" : "y"
      if (axisLock.current === "x") dy = 0
      else dx = 0
    } else axisLock.current = null
    setPage(`background.${backgroundLayout}.${phase}Transform.offsetPos.x`, round((offset.x || 0) + dx / ratio))
    // the authored y grows upwards, the screen's downwards
    setPage(`background.${backgroundLayout}.${phase}Transform.offsetPos.y`, round((offset.y || 0) - dy / ratio))
  }
  const scaleBackground = (delta, fast) => {
    const rate = fast ? 4000 : 1000
    const next = Math.max(0.05, round((transform.scale || 1) * (1 - delta / rate)))
    setPage(`background.${backgroundLayout}.${phase}Transform.scale`, next)
  }

  /**
   * Fit the picture to every layout, the way pressing C does in the app
   * editor: the scale that covers the shapes the layout is shown at, centred.
   */
  const fitToLayouts = () => {
    if (!size.width || !size.height) return
    const aspect = size.width / size.height
    for (const name of LAYOUTS) {
      if (!page?.background?.[name]) continue
      const range = ASPECT_RANGES[name]
      const scale = Math.max(aspect / range.min, aspect / range.max)
      for (const which of PHASES) setPage(`background.${name}.${which}Transform`, { scale: round(scale), offsetPos: { x: 0, y: 0 } })
    }
  }

  const subtitle = page?.subtitle?.[subtitleLayout] || {}
  const box = subtitle.renderBox || {}
  const moveText = (dx, dy) => {
    setPage(`subtitle.${subtitleLayout}.renderBox.left`, Math.round((box.left || 0) + dx / design))
    setPage(`subtitle.${subtitleLayout}.renderBox.top`, Math.round((box.top || 0) - dy / design))
  }
  const resizeText = (dx, dy) => {
    setPage(`subtitle.${subtitleLayout}.renderBox.width`, Math.max(40, Math.round((box.width || 350) + dx / design)))
    setPage(`subtitle.${subtitleLayout}.renderBox.height`, Math.max(20, Math.round((box.height || 60) + dy / design)))
  }

  const moveTrigger = (index, area) => (dx, dy) => {
    setPage(`triggerAreas.${index}.area.left`, Math.round((area.left || 0) + dx / design))
    setPage(`triggerAreas.${index}.area.top`, Math.round((area.top || 0) - dy / design))
  }
  const resizeTrigger = (index, area) => (dx, dy) => {
    setPage(`triggerAreas.${index}.area.width`, Math.max(10, Math.round((area.width || 0) + dx / design)))
    setPage(`triggerAreas.${index}.area.height`, Math.max(10, Math.round((area.height || 0) + dy / design)))
  }

  const backgroundDrag = useDrag(moveBackground, { onEnd: () => (axisLock.current = null) })
  const textDrag = useDrag(moveText)
  const textResize = useDrag(resizeText)

  if (!enabled || !viewport.width || !page) return null

  const textLeft = (box.left || 0) * design + viewport.width / 2
  const textTop = viewport.height / 2 - (box.top || 0) * design
  const textWidth = Math.max(40, (box.width || 350) * design)
  const textHeight = Math.max(20, (box.height || 60) * design)

  /** Drawing a new tap area: press, drag a rectangle, let go. */
  const drawArea = {
    onPointerDown: (event) => {
      if (tool !== "trigger") return
      event.preventDefault()
      const rect = event.currentTarget.getBoundingClientRect()
      setDrawing({ x: event.clientX - rect.left, y: event.clientY - rect.top, w: 0, h: 0 })
    },
    onPointerMove: (event) => {
      if (!drawing) return
      const rect = event.currentTarget.getBoundingClientRect()
      setDrawing((current) => ({ ...current, w: event.clientX - rect.left - current.x, h: event.clientY - rect.top - current.y }))
    },
    onPointerUp: async () => {
      if (!drawing) return
      const shape = drawing
      setDrawing(null)
      if (Math.abs(shape.w) < 8 || Math.abs(shape.h) < 8) return
      const order = await onAddTrigger?.()
      if (order === null || order === undefined) return
      const left = Math.min(shape.x, shape.x + shape.w)
      const top = Math.min(shape.y, shape.y + shape.h)
      setPage(`triggerAreas.${order}.area`, {
        left: Math.round((left - viewport.width / 2) / design),
        top: Math.round((viewport.height / 2 - top) / design),
        width: Math.round(Math.abs(shape.w) / design),
        height: Math.round(Math.abs(shape.h) / design),
      })
    },
  }

  return (
    <>
      {/* the picture: with the Select tool it is dragged and scaled; with the
          Tap area tool a press draws a new zone on it */}
      <div
        className={twMerge("absolute inset-0", tool === "trigger" ? "cursor-crosshair" : tool === "select" ? "cursor-move" : "cursor-default")}
        onWheel={(event) => {
          if (tool !== "select") return
          event.preventDefault()
          scaleBackground(event.deltaY, event.altKey)
        }}
        onClick={() => onSelect?.({ kind: "background" })}
        {...(tool === "select" ? backgroundDrag : {})}
        {...(tool === "trigger" ? drawArea : {})}
        data-testid="stage-background-handle"
        title={
          tool === "trigger"
            ? t`Drag to draw a tap area`
            : t("Drag to move the %s keyframe · wheel to scale · Shift locks an axis · Alt scales faster", t(phase))
        }
      />

      {drawing && (
        <div
          className="absolute border-2 border-dashed border-admin_warning bg-admin_warning/20 pointer-events-none"
          style={{
            left: Math.min(drawing.x, drawing.x + drawing.w),
            top: Math.min(drawing.y, drawing.y + drawing.h),
            width: Math.abs(drawing.w),
            height: Math.abs(drawing.h),
          }}
        />
      )}

      {/* which keyframe is being set, and the fit that C performs in the app */}
      <div className="absolute left-2 top-2 inline-flex p-1 rounded-lg bg-black/70 gap-1 text-white items-center">
        {PHASES.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => {
              onPhase(name)
              onSelect?.({ kind: "background" })
            }}
            className={twMerge("px-3 h-7 rounded-md text-xs capitalize", phase === name ? "bg-white text-black font-semibold" : "hover:bg-white/15")}
            data-testid={`phase-${name}`}
          >
            {t(name)}
          </button>
        ))}
        <button type="button" onClick={fitToLayouts} className="px-2 h-7 rounded-md text-xs hover:bg-white/15" title={t`Fit the picture to every layout`} data-testid="fit-layouts">
          {t`fit`}
        </button>
        <span className="px-2 h-7 inline-flex items-center text-[11px] text-white/70 tabular-nums">
          x {round(offset.x || 0)} · y {round(offset.y || 0)} · ×{round(transform.scale || 1)}
        </span>
      </div>

      {/* the words, where they will be read */}
      {(subtitle.text || tool === "text") && (
        <div
          className={twMerge(
            "absolute border border-dashed bg-white/5",
            selection.kind === "text" ? "border-admin_primary" : "border-white/80"
          )}
          style={{ left: textLeft, top: textTop, width: textWidth, height: textHeight }}
          data-testid="stage-text-box"
        >
          <div
            className="absolute inset-0 cursor-move"
            onClick={() => onSelect?.({ kind: "text" })}
            {...(tool === "select" || tool === "text" ? textDrag : {})}
            title={t`Drag the text box`}
          />
          {(tool === "select" || tool === "text") && (
            <div className="absolute -right-1 -bottom-1 w-3 h-3 bg-white border border-black cursor-se-resize" {...textResize} title={t`Resize the text box`} />
          )}
        </div>
      )}

      {/* the tap zones */}
      {(page.triggerAreas || []).map((trigger, index) => {
        const area = trigger.area || {}
        const left = (area.left || 0) * design + viewport.width / 2
        const top = viewport.height / 2 - (area.top || 0) * design
        const chosen = selection.kind === "trigger" && (selection.order ?? 0) === index
        return (
          <div
            key={index}
            className={twMerge("absolute border-2 border-dashed", chosen ? "border-admin_primary bg-admin_primary/15" : "border-admin_warning/90 bg-admin_warning/10")}
            style={{ left, top, width: Math.max(10, (area.width || 0) * design), height: Math.max(10, (area.height || 0) * design) }}
            data-testid="stage-trigger-box"
          >
            <span className="absolute -top-5 left-0 text-[10px] px-1 rounded bg-admin_warning text-black">
              {trigger.gotoPage ? t("→ scene %s", trigger.gotoPage) : t`sound`}
            </span>
            <TriggerHandles
              onMove={moveTrigger(index, area)}
              onResize={resizeTrigger(index, area)}
              onSelect={() => onSelect?.({ kind: "trigger", order: index })}
              draggable={tool === "select" || tool === "trigger"}
            />
          </div>
        )
      })}
    </>
  )
}

/** A zone moves by its middle and resizes by its corner. */
const TriggerHandles = ({ onMove, onResize, onSelect, draggable }) => {
  const t = useProductEditorTexts()
  const move = useDrag(onMove)
  const resize = useDrag(onResize)
  return (
    <>
      <div className="absolute inset-0 cursor-move" onClick={onSelect} {...(draggable ? move : {})} title={t`Drag the tap area`} />
      {draggable && <div className="absolute -right-1 -bottom-1 w-3 h-3 bg-admin_warning border border-black cursor-se-resize" {...resize} title={t`Resize the tap area`} />}
    </>
  )
}
